import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalFooter,
  PasswordInput,
  Text,
  TextInput,
} from '@razorpay/blade/components';
import { usePayDetails } from 'components/PayDetails/context/PayDetailsContext';
import { NON_DIGIT_REGEX, NPS_PRAN_NUMBER_REGEX } from 'utils/regexes';

type EnterPRANModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  onConfirm: (pran: string) => void;
  isLoading: boolean;
  hasError: boolean;
};

export const EnterPRANModal = ({
  isOpen,
  onDismiss,
  onConfirm,
  isLoading,
  hasError,
}: EnterPRANModalProps) => {
  const {
    userData: { npsPran },
  } = usePayDetails();

  const [pran, setPran] = useState(npsPran ?? '');
  const [confirmPran, setConfirmPran] = useState(npsPran ?? '');

  const PRAN_LENGTH = 12;
  const isValidPran = NPS_PRAN_NUMBER_REGEX.test(pran);
  const isPranMismatch = confirmPran.length > 0 && pran !== confirmPran;
  const isConfirmDisabled = !isValidPran || !confirmPran || pran !== confirmPran;

  const handlePranChange = ({ value }: { value?: string }) => {
    const digitsOnly = (value ?? '').replace(NON_DIGIT_REGEX, '');
    setPran(digitsOnly);
  };

  const handleConfirmPranChange = ({ value }: { value?: string }) => {
    const digitsOnly = (value ?? '').replace(NON_DIGIT_REGEX, '');
    setConfirmPran(digitsOnly);
  };

  const handleConfirm = () => {
    onConfirm(pran);
  };

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} accessibilityLabel="Enter PRAN">
      <ModalBody>
        <Box display="flex" flexDirection="column" gap="spacing.5">
          {hasError && (
            <Alert
              description={
                'We are some facing some error saving your PRAN. Please try again later.'
              }
              color="negative"
              isFullWidth
              isDismissible={false}
            />
          )}
          <Box display="flex" flexDirection="column" gap="spacing.2">
            <Heading size="large" weight="semibold">
              Enter PRAN
            </Heading>
            <Text size="medium" color="surface.text.gray.muted">
              PRAN (Permanent Retirement Account Number) is your unique 12-digit NPS account number.
              Without PRAN, we cannot process your NPS contribution.
            </Text>
          </Box>

          <Box display="flex" flexDirection="column" gap="spacing.5">
            <PasswordInput
              label="Enter PRAN"
              placeholder="Enter 12-digit PRAN"
              value={pran}
              maxCharacters={PRAN_LENGTH}
              showRevealButton={false}
              onChange={handlePranChange}
              validationState={pran.length > 0 && !isValidPran ? 'error' : 'none'}
              errorText="PRAN must be exactly 12 digits"
              isRequired={true}
              necessityIndicator="required"
            />
            <TextInput
              label="Re-enter to confirm"
              placeholder="Re-enter PRAN"
              value={confirmPran}
              maxCharacters={PRAN_LENGTH}
              onChange={handleConfirmPranChange}
              validationState={isPranMismatch ? 'error' : 'none'}
              errorText="PRAN does not match"
              isRequired={true}
              necessityIndicator="required"
            />
          </Box>
        </Box>
      </ModalBody>

      <ModalFooter>
        <Box display="flex">
          <Button
            onClick={handleConfirm}
            isDisabled={isConfirmDisabled || isLoading}
            isLoading={isLoading}
            isFullWidth>
            Confirm
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
